import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import FirebaseChatRepository from '../api/FirebaseChatRepository';
import SendMessageUseCase from '../use-cases/SendMessageUseCase';
import GetOrCreateOneToOneConversationUseCase from '../use-cases/GetOrCreateOneToOneConversationUseCase';
import EditMessageUseCase from '../use-cases/EditMessageUseCase';
import DeleteMessageUseCase from '../use-cases/DeleteMessageUseCase';
import GetConversationByIdUseCase from '../use-cases/GetConversationByIdUseCase';

const MESSAGES_PER_PAGE = 50;

const useChat = (currentUserId, currentUserName, selectedUserId, selectedUserName, selectedConversationId = null) => {
  const [messages, setMessages] = useState([]);
  const [chatId, setChatId] = useState(null);
  const [loadingChat, setLoadingChat] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [error, setError] = useState(null);
  const oldestDocRef = useRef(null);

  const chatRepository = useMemo(() => new FirebaseChatRepository(), []);
  const sendMessageUseCase = useMemo(() => new SendMessageUseCase(chatRepository), [chatRepository]);
  const getOrCreateOneToOneConversationUseCase = useMemo(() => new GetOrCreateOneToOneConversationUseCase(chatRepository), [chatRepository]);
  const editMessageUseCase = useMemo(() => new EditMessageUseCase(chatRepository), [chatRepository]);
  const deleteMessageUseCase = useMemo(() => new DeleteMessageUseCase(chatRepository), [chatRepository]);
  const getConversationByIdUseCase = useMemo(() => new GetConversationByIdUseCase(chatRepository), [chatRepository]);

  const editMessage = useCallback(async (messageId, newText) => {
    if (!currentUserId || !chatId || !messageId || !newText.trim()) return;
    setError(null);
    try {
      await editMessageUseCase.execute(chatId, messageId, newText);
    } catch (err) {
      console.error("Error editing message:", err);
      setError("Failed to edit message.");
    }
  }, [currentUserId, chatId, editMessageUseCase]);

  const deleteMessage = useCallback(async (messageId) => {
    if (!currentUserId || !chatId || !messageId) return;
    setError(null);
    try {
      await deleteMessageUseCase.execute(chatId, messageId);
    } catch (err) {
      console.error("Error deleting message:", err);
      setError("Failed to delete message.");
    }
  }, [currentUserId, chatId, deleteMessageUseCase]);

  const sendMessage = useCallback(async (text, type = 'text', imageFile = null) => {
    if (!currentUserId || !chatId || (!text.trim() && !imageFile)) return;
    setError(null);
    try {
      await sendMessageUseCase.execute(chatId, currentUserId, currentUserName, text, type, imageFile);
    } catch (err) {
      console.error("Error sending message:", err);
      setError("Failed to send message.");
    }
  }, [currentUserId, currentUserName, chatId, sendMessageUseCase]);

  const loadOlderMessages = useCallback(async () => {
    if (!chatId || !oldestDocRef.current || loadingOlder || !hasMoreMessages) return;
    setLoadingOlder(true);
    try {
      const { messages: olderMessages, firstDoc, hasReachedEnd } = await chatRepository.getOlderMessages(
        chatId, oldestDocRef.current, MESSAGES_PER_PAGE
      );
      if (firstDoc) oldestDocRef.current = firstDoc;
      setHasMoreMessages(!hasReachedEnd);
      setMessages((prev) => [...olderMessages, ...prev]);
    } catch (err) {
      console.error("Error loading older messages:", err);
    } finally {
      setLoadingOlder(false);
    }
  }, [chatId, loadingOlder, hasMoreMessages, chatRepository]);

  useEffect(() => {
    let unsubscribe;
    const setupChat = async () => {
      setLoadingChat(true);
      setError(null);
      setMessages([]);
      setHasMoreMessages(true);
      oldestDocRef.current = null;
      let convId = null;

      try {
        if (selectedConversationId) {
          convId = selectedConversationId;
        } else if (currentUserId && selectedUserId) {
          const conversation = await getOrCreateOneToOneConversationUseCase.execute(
            currentUserId, currentUserName, selectedUserId, selectedUserName
          );
          convId = conversation.id;
        } else {
          setLoadingChat(false);
          setError("Invalid chat parameters.");
          return;
        }

        setChatId(convId);

        // Listen to the most recent messages only
        unsubscribe = chatRepository.onRecentMessagesUpdate(convId, MESSAGES_PER_PAGE, (fetchedMessages, firstDoc, hasReachedEnd) => {
          setMessages(fetchedMessages);
          if (firstDoc) oldestDocRef.current = firstDoc;
          setHasMoreMessages(!hasReachedEnd);
        });
      } catch (err) {
        console.error("Error setting up chat:", err);
        setError("Failed to load chat.");
      } finally {
        setLoadingChat(false);
      }
    };

    setupChat();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [currentUserId, currentUserName, selectedUserId, selectedUserName, selectedConversationId, getOrCreateOneToOneConversationUseCase, chatRepository, getConversationByIdUseCase]);

  return { messages, sendMessage, editMessage, deleteMessage, chatId, loadingChat, loadingOlder, hasMoreMessages, loadOlderMessages, error };
};

export default useChat;
